import { createSlice } from '@reduxjs/toolkit';

import type { JsonFailure } from '@skynet/data-ws-core';
import { isOkCommand, parseResponse, registerErrorMessage } from '@skynet/data-ws-core';
import { actionOnMessage } from '@skynet/data-ws-mm';

import type {
  BalanceAlertRule,
  BalanceAlertRulesState,
  JsonBalanceAlertRule,
  JsonBalanceAlertRulesSuccess,
} from './types';
import { BalanceAlertRulesCommands } from './types';
import {
  addBalanceAlertRule,
  deleteBalanceAlertRule,
  toggleBalanceAlertRule,
  updateBalanceAlertRule,
} from './actions';

const allowedCommands = Object.values(BalanceAlertRulesCommands);

// `parseResponse`'s error branch only fires for commands registered here; without these the
// server's validation errors (empty token, limit <= 0, a comma in a recipient) are dropped.
const DEFAULT_ERROR_DESCRIPTION = 'Sorry, an unexpected error has occurred';

registerErrorMessage(BalanceAlertRulesCommands.AddBalanceAlertRule, {
  title: 'Add Balance Alert Rule Error',
  getDescription: (json: JsonFailure) => json.message ?? DEFAULT_ERROR_DESCRIPTION,
});
registerErrorMessage(BalanceAlertRulesCommands.UpdateBalanceAlertRule, {
  title: 'Update Balance Alert Rule Error',
  getDescription: (json: JsonFailure) => json.message ?? DEFAULT_ERROR_DESCRIPTION,
});
registerErrorMessage(BalanceAlertRulesCommands.DeleteBalanceAlertRule, {
  title: 'Delete Balance Alert Rule Error',
  getDescription: (json: JsonFailure) => json.message ?? DEFAULT_ERROR_DESCRIPTION,
});

/** Lists are never `null` in the store, so nothing that renders a rule has to guard them. */
const normalizeRule = (rule: JsonBalanceAlertRule): BalanceAlertRule => ({
  ...rule,
  usersToNotify: rule.usersToNotify ?? [],
  telegramGroupsToNotify: rule.telegramGroupsToNotify ?? [],
  keyWordsAccountFilter: rule.keyWordsAccountFilter ?? [],
  idAccountFilter: rule.idAccountFilter ?? [],
});

/**
 * Pushes after the ack that a switch flip or a delete outlives when they keep contradicting
 * it. A push serialised just before the server applied the change can still carry the old
 * state, so only the second one is trusted.
 */
const PUSHES_TO_TRUST = 2;

/**
 * Drops what a push has caught up with: a switch flip once the pushed rule agrees, a deleted
 * rule once the push stops carrying it — or either after {@link PUSHES_TO_TRUST} pushes past
 * the ack that still disagree (someone changed the rule since, or the delete was lost; the
 * rule is shown again rather than left invisible and unmanageable).
 */
const reconcileWithPush = (state: BalanceAlertRulesState) => {
  const pushedEnabledById = new Map(state.rules.map((rule) => [rule.id, rule.enabled]));
  for (const [key, pending] of Object.entries(state.pendingEnabled)) {
    const ruleId = Number(key);
    const pushed = pushedEnabledById.get(ruleId);
    if (pending.pushesAfterAck !== null) pending.pushesAfterAck += 1;
    if (
      pushed === undefined ||
      pushed === pending.enabled ||
      (pending.pushesAfterAck ?? 0) >= PUSHES_TO_TRUST
    ) {
      delete state.pendingEnabled[ruleId];
    }
  }
  for (const [key, pushesAfterAck] of Object.entries(state.deletedRules)) {
    const ruleId = Number(key);
    if (!pushedEnabledById.has(ruleId) || pushesAfterAck + 1 >= PUSHES_TO_TRUST) {
      delete state.deletedRules[ruleId];
    } else {
      state.deletedRules[ruleId] = pushesAfterAck + 1;
    }
  }
};

export const initialState: BalanceAlertRulesState = {
  projectID: undefined,
  rules: [],
  status: null,
  updateStatus: null,
  toggleStatus: null,
  deleteStatus: null,
  error: null,
  updateError: null,
  toggleError: null,
  deleteError: null,
  togglingRuleId: null,
  deletingRuleId: null,
  pendingEnabled: {},
  deletedRules: {},
};

const balanceAlertRulesSlice = createSlice({
  name: 'balanceAlertRules',
  initialState,
  reducers: {
    resetAddStatus(state) {
      state.status = null;
      state.error = null;
    },
    resetUpdateStatus(state) {
      state.updateStatus = null;
      state.updateError = null;
    },
    resetToggleStatus(state) {
      state.toggleStatus = null;
      state.toggleError = null;
    },
    resetDeleteStatus(state) {
      state.deleteStatus = null;
      state.deleteError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addBalanceAlertRule, (state) => {
      state.status = 'pending';
      state.error = null;
    });

    builder.addCase(updateBalanceAlertRule, (state) => {
      state.updateStatus = 'pending';
      state.updateError = null;
    });

    builder.addCase(toggleBalanceAlertRule, (state, { payload }) => {
      state.toggleStatus = 'pending';
      state.toggleError = null;
      state.togglingRuleId = payload.ruleID;
      state.pendingEnabled[payload.ruleID] = { enabled: payload.enabled, pushesAfterAck: null };
    });

    builder.addCase(deleteBalanceAlertRule, (state, { payload }) => {
      state.deleteStatus = 'pending';
      state.deleteError = null;
      state.deletingRuleId = payload.ruleID;
    });

    builder.addCase(actionOnMessage, (state, action) => {
      const { json, skipProcessing, error, errorCommand } = parseResponse(
        action.payload,
        allowedCommands,
      );

      if (skipProcessing || !json) return;

      const { command, okCommand } = json;

      if (error) {
        if (errorCommand === BalanceAlertRulesCommands.AddBalanceAlertRule) {
          state.error = error;
          state.status = 'error';
        } else if (errorCommand === BalanceAlertRulesCommands.UpdateBalanceAlertRule) {
          if (state.toggleStatus === 'pending') {
            state.toggleError = error;
            state.toggleStatus = 'error';
            if (state.togglingRuleId !== null) delete state.pendingEnabled[state.togglingRuleId];
            state.togglingRuleId = null;
          } else {
            state.updateError = error;
            state.updateStatus = 'error';
          }
        } else if (errorCommand === BalanceAlertRulesCommands.DeleteBalanceAlertRule) {
          state.deleteError = error;
          state.deleteStatus = 'error';
          state.deletingRuleId = null;
        }
        return;
      }

      if (command === BalanceAlertRulesCommands.BalanceAlertRules) {
        // Pushed every 5 seconds to each Terms socket, so this replaces the list wholesale.
        const { projectID, rules }: JsonBalanceAlertRulesSuccess = json;
        if (state.projectID !== projectID) {
          state.pendingEnabled = {};
          state.deletedRules = {};
        }
        state.projectID = projectID;
        state.rules = (rules ?? []).map(normalizeRule);
        reconcileWithPush(state);
        return;
      }

      if (isOkCommand(command)) {
        if (okCommand === BalanceAlertRulesCommands.AddBalanceAlertRule) {
          state.status = 'success';
        } else if (okCommand === BalanceAlertRulesCommands.UpdateBalanceAlertRule) {
          if (state.toggleStatus === 'pending') {
            state.toggleStatus = 'success';
            const pending =
              state.togglingRuleId === null
                ? undefined
                : state.pendingEnabled[state.togglingRuleId];
            if (pending) pending.pushesAfterAck = 0;
            state.togglingRuleId = null;
          } else {
            state.updateStatus = 'success';
          }
        } else if (okCommand === BalanceAlertRulesCommands.DeleteBalanceAlertRule) {
          state.deleteStatus = 'success';
          if (state.deletingRuleId !== null) state.deletedRules[state.deletingRuleId] = 0;
          state.deletingRuleId = null;
        }
      }
    });
  },
});

export const balanceAlertRulesActions = {
  ...balanceAlertRulesSlice.actions,
  addBalanceAlertRule,
  updateBalanceAlertRule,
  toggleBalanceAlertRule,
  deleteBalanceAlertRule,
};

export default balanceAlertRulesSlice.reducer;
